import React, { useState } from 'react';
import { Alert, Button, Group, Modal, Select, Text } from '@mantine/core';
import { useGetPuck } from '@puckeditor/core';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import ImagePointSurface from '../../../puck/fields/ImagePointSurface';
import { hotspotGeometry, hotspotNumberSide } from './hotspotPoints';
import { HotspotPositionMarker } from './HotspotMarker';
import HotspotRectangleSurface from './HotspotRectangleSurface';
import HotspotPositionPreview from './HotspotPositionPreview';

export default function HotspotPositionField({ point, index }) {
  const { itemProps } = useSelectedItemProps();
  const getPuck = useGetPuck();
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const available = !!itemProps.src && (!itemProps.srcVariable?.variableKey || !!itemProps.srcVariable.variableValue);
  const close = () => setSession(null);
  const confirm = () => {
    const { getSelectorForId, getItemById, dispatch } = getPuck();
    const selector = getSelectorForId(session.blockId);
    const item = selector ? getItemById(session.blockId) : null;
    if (!item || item.props.src !== session.src || !item.props.points?.some(({ id }) => id === session.pointId)) {
      setError('L’image ou le repère a changé. Fermez cette fenêtre et sélectionnez à nouveau le repère.');
      return;
    }
    dispatch({ type: 'replace', destinationZone: selector.zone, destinationIndex: selector.index, data: { ...item, props: { ...item.props, points: item.props.points.map((current) => current.id === session.pointId ? { ...current, ...session.position } : current) } } });
    close();
  };
  return (
    <>
      <Button size="xs" variant="light" disabled={!available} onClick={() => {
        setError('');
        setReady(false);
        setSession({ blockId: itemProps.id, pointId: point.id, src: itemProps.src, points: itemProps.points, position: { ...hotspotGeometry(point), ...(point.shape === 'rectangle' ? { numberSide: hotspotNumberSide(point) } : {}) } });
      }}>Modifier la position du repère {index + 1}</Button>
      {!available && <Text size="xs" c="dimmed">Choisissez une image disponible pour placer ce repère.</Text>}
      <Modal opened={!!session} onClose={close} title={`Position du repère ${index + 1}`} size="xl" centered>
        {session && <>
          <Text size="sm" mb="sm">{point.shape === 'rectangle' ? 'Déplacez l’encadré en maintenant le clic à l’intérieur. Tirez un coin pour modifier sa largeur et sa hauteur (minimum 5 %). Les flèches ajustent le contrôle sélectionné.' : 'Cliquez sur le sujet ou déplacez le repère avec les flèches du clavier. Les autres repères restent en place.'}</Text>
          {error && <Alert color="orange" mb="sm">{error}</Alert>}
          <HotspotPositionPreview blockId={itemProps.id} appearance={itemProps}>
            {point.shape === 'rectangle' ? (
              <>
                <HotspotRectangleSurface src={session.src} point={{ ...point, ...session.position }} points={session.points} index={index} onReady={setReady}
                  onChange={(position) => setSession((current) => ({ ...current, position: { ...current.position, ...position } }))} />
                <Select mt="md" label="Position du numéro" value={session.position.numberSide} allowDeselect={false} comboboxProps={{ withinPortal: false }}
                  data={[{ value: 'left', label: 'À gauche' }, { value: 'right', label: 'À droite' }, { value: 'top', label: 'Au-dessus' }, { value: 'bottom', label: 'En dessous' }]}
                  onChange={(numberSide) => setSession((current) => ({ ...current, position: { ...current.position, numberSide } }))} />
                <Text size="xs" c="dimmed" mt="xs">Si le côté choisi manque de place, le numéro est replacé automatiquement pour rester dans l’image.</Text>
              </>
            ) : (
              <ImagePointSurface src={session.src} value={session.position} onChange={(position) => setSession((current) => ({ ...current, position }))} label={`Placer le repère ${index + 1}`} autoFocus onReady={setReady}
                renderMarkers={(position) => session.points.map((current, i) => <HotspotPositionMarker key={current.id} point={current.id === session.pointId ? { ...current, ...position } : current} index={i} selected={current.id === session.pointId} />)} />
            )}
          </HotspotPositionPreview>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={close}>Annuler</Button>
            <Button disabled={!ready || !!error} onClick={confirm}>Valider la position</Button>
          </Group>
        </>}
      </Modal>
    </>
  );
}
